'use client';

import { useState } from 'react';

export default function AgeCalculator() {
  const [birthDate, setBirthDate] = useState('');
  const [age, setAge] = useState<{
    years: number;
    months: number;
    days: number;
  } | null>(null);

  const calculateAge = () => {
    if (!birthDate) return;

    const birth = new Date(birthDate);
    const today = new Date();

    if (birth > today) {
      alert('Birth date cannot be in the future!');
      return;
    }

    let years = today.getFullYear() - birth.getFullYear();
    let months = today.getMonth() - birth.getMonth();
    let days = today.getDate() - birth.getDate();

    if (days < 0) {
      months -= 1;
      const prevMonth = new Date(today.getFullYear(), today.getMonth(), 0);
      days += prevMonth.getDate();
    }

    if (months < 0) {
      years -= 1;
      months += 12;
    }

    setAge({ years, months, days });
  };

  const resetCalculator = () => {
    setBirthDate('');
    setAge(null);
  };

  const getNextBirthday = () => {
    if (!birthDate) return null;

    const birth = new Date(birthDate);
    const today = new Date();

    let nextBirthday = new Date(today.getFullYear(), birth.getMonth(), birth.getDate());

    if (nextBirthday < today) {
      nextBirthday = new Date(today.getFullYear() + 1, birth.getMonth(), birth.getDate());
    }

    const daysUntil = Math.ceil((nextBirthday.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

    return {
      date: nextBirthday.toLocaleDateString('en-US', { month: 'long', day: 'numeric' }),
      daysUntil
    };
  };

  const nextBirthday = getNextBirthday();

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
          Age Calculator
        </h2>

        <div className="space-y-6">
          <div>
            <label htmlFor="birthDate" className="block text-sm font-medium text-gray-700 mb-2">
              Date of Birth
            </label>
            <input
              type="date"
              id="birthDate"
              value={birthDate}
              onChange={(e) => setBirthDate(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
              max={new Date().toISOString().split('T')[0]}
            />
          </div>

          <div className="flex gap-4">
            <button
              onClick={calculateAge}
              disabled={!birthDate}
              className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              Calculate Age
            </button>

            <button
              onClick={resetCalculator}
              className="px-6 py-3 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Reset
            </button>
          </div>

          {age && (
            <div className="mt-6 p-6 bg-gray-50 rounded-lg">
              <div className="text-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Your Age</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-blue-600">{age.years}</p>
                    <p className="text-sm text-gray-600">Years</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-600">{age.months}</p>
                    <p className="text-sm text-gray-600">Months</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-purple-600">{age.days}</p>
                    <p className="text-sm text-gray-600">Days</p>
                  </div>
                </div>
              </div>

              {nextBirthday && (
                <div className="mt-4 p-4 bg-white rounded-lg border">
                  <p className="text-center text-gray-700">
                    <span className="font-medium">Next birthday:</span> {nextBirthday.date}
                    <br />
                    <span className="text-blue-600 font-medium">
                      {nextBirthday.daysUntil} days away!
                    </span>
                  </p>
                </div>
              )}

              <div className="mt-4 text-sm text-gray-600">
                <p><strong>Total days lived:</strong> {age.years * 365 + age.months * 30 + age.days}</p>
                <p><strong>Total months lived:</strong> {age.years * 12 + age.months}</p>
                <p><strong>Total weeks lived:</strong> {Math.floor((age.years * 365 + age.months * 30 + age.days) / 7)}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}